import * as THREE from 'three';
import { createGlowMaterial, createMatteMaterial } from '../../visual';

export interface WanderingNpcOptions {
  readonly suitColor: number;
  readonly suitDarkColor: number;
  readonly accentColor: number;
  readonly skinColor: number;
  readonly hairColor?: number;
  readonly kind: 'human-civ' | 'human-engineer' | 'alien-tall' | 'alien-short' | 'robed' | 'heavy';
}

/**
 * A wandering civilian NPC for the station floor. Full-body model with a
 * walk cycle, distinct per-kind silhouette, and small idle details.
 */
export class WanderingNpc {
  readonly object3D: THREE.Group;
  private readonly torso: THREE.Group;
  private readonly leftLeg: THREE.Group;
  private readonly rightLeg: THREE.Group;
  private readonly leftArm: THREE.Group;
  private readonly rightArm: THREE.Group;
  private readonly waypoints: readonly THREE.Vector3[];
  private readonly speed: number;
  private waypointIndex = 0;
  private phase = Math.random() * Math.PI * 2;
  private pauseTimer = 0;

  constructor(waypoints: readonly THREE.Vector3[], options: WanderingNpcOptions) {
    this.waypoints = waypoints;
    this.speed = 0.9 + Math.random() * 0.7;
    this.object3D = new THREE.Group();
    this.object3D.name = `wandering-npc:${options.kind}`;
    if (waypoints[0]) this.object3D.position.copy(waypoints[0]);

    const suitMat = createMatteMaterial({
      color: options.suitColor,
      roughness: 0.72
    });
    const suitDarkMat = createMatteMaterial({
      color: options.suitDarkColor,
      roughness: 0.82
    });
    const skinMat = createMatteMaterial({
      color: options.skinColor,
      roughness: 0.65
    });
    const hairMat = createMatteMaterial({
      color: options.hairColor ?? 0x1a1410,
      roughness: 0.82
    });
    const accentMat = createMatteMaterial({
      color: options.accentColor,
      roughness: 0.4,
      emissive: options.accentColor,
      emissiveIntensity: 0.28
    });
    const eyeMat = createGlowMaterial(options.accentColor, 0.95, true);

    const isAlien = options.kind.startsWith('alien');
    const isRobed = options.kind === 'robed' || options.kind === 'alien-tall';
    const isHeavy = options.kind === 'heavy';
    const tall = options.kind === 'alien-tall' || options.kind === 'heavy';

    // Legs — hidden under robe for alien-tall/robed, boxy for heavy.
    this.leftLeg = new THREE.Group();
    this.rightLeg = new THREE.Group();
    if (!isRobed) {
      const legWidth = isHeavy ? 0.13 : 0.1;
      const legLower = isHeavy ? 0.08 : 0.06;
      const thighGeo = new THREE.CylinderGeometry(legWidth, legLower, 0.45, 10);
      const shinGeo = new THREE.CylinderGeometry(legLower, legLower * 0.95, 0.45, 10);
      const bootGeo = new THREE.BoxGeometry(0.18, 0.12, 0.26);

      const thighL = new THREE.Mesh(thighGeo, suitDarkMat);
      thighL.position.y = -0.22;
      this.leftLeg.add(thighL);
      const shinL = new THREE.Mesh(shinGeo, suitDarkMat);
      shinL.position.y = -0.68;
      this.leftLeg.add(shinL);
      const bootL = new THREE.Mesh(bootGeo, createMatteMaterial({ color: 0x0a0d14, roughness: 0.8 }));
      bootL.position.y = -0.96;
      this.leftLeg.add(bootL);
      this.leftLeg.position.set(-0.12, 0.95, 0);
      this.object3D.add(this.leftLeg);

      const thighR = new THREE.Mesh(thighGeo, suitDarkMat);
      thighR.position.y = -0.22;
      this.rightLeg.add(thighR);
      const shinR = new THREE.Mesh(shinGeo, suitDarkMat);
      shinR.position.y = -0.68;
      this.rightLeg.add(shinR);
      const bootR = new THREE.Mesh(bootGeo, createMatteMaterial({ color: 0x0a0d14, roughness: 0.8 }));
      bootR.position.y = -0.96;
      this.rightLeg.add(bootR);
      this.rightLeg.position.set(0.12, 0.95, 0);
      this.object3D.add(this.rightLeg);
    } else {
      // Robe cone from hip down.
      const robe = new THREE.Mesh(
        new THREE.ConeGeometry(0.42, 1.1, 16, 1, true),
        suitMat
      );
      robe.position.y = 0.4;
      this.object3D.add(robe);

      const robeTrim = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.025, 8, 28),
        accentMat
      );
      robeTrim.rotation.x = Math.PI / 2;
      robeTrim.position.y = -0.15;
      this.object3D.add(robeTrim);
    }

    // Torso.
    this.torso = new THREE.Group();
    const torsoBaseY = tall ? 1.15 : 1.05;
    this.torso.position.y = torsoBaseY;
    this.torso.userData['baseY'] = torsoBaseY;
    this.object3D.add(this.torso);

    const chestGeo = isHeavy
      ? new THREE.BoxGeometry(0.6, 0.72, 0.38)
      : new THREE.BoxGeometry(0.48, 0.65, 0.3);
    const chest = new THREE.Mesh(chestGeo, suitMat);
    chest.position.y = 0.2;
    this.torso.add(chest);

    const collar = new THREE.Mesh(
      new THREE.BoxGeometry(chestGeo.parameters.width + 0.04, 0.08, chestGeo.parameters.depth + 0.04),
      accentMat
    );
    collar.position.y = 0.55;
    this.torso.add(collar);

    // Chest strap (civilian).
    if (options.kind === 'human-civ' || options.kind === 'human-engineer') {
      const strap = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 0.62, 0.04),
        suitDarkMat
      );
      strap.position.set(-0.1, 0.2, 0.16);
      strap.rotation.z = 0.25;
      this.torso.add(strap);
    }

    if (isHeavy) {
      // Pauldrons for heavy kind.
      for (const side of [-1, 1] as const) {
        const pad = new THREE.Mesh(
          new THREE.SphereGeometry(0.2, 12, 8),
          suitDarkMat
        );
        pad.scale.set(1.1, 0.65, 1.1);
        pad.position.set(side * 0.3, 0.55, 0);
        this.torso.add(pad);
      }
    }

    // Arms.
    this.leftArm = this.buildWanderArm(suitMat, suitDarkMat, skinMat);
    this.leftArm.position.set(-(chestGeo.parameters.width / 2 + 0.05), 0.5, 0);
    this.torso.add(this.leftArm);

    this.rightArm = this.buildWanderArm(suitMat, suitDarkMat, skinMat);
    this.rightArm.position.set(chestGeo.parameters.width / 2 + 0.05, 0.5, 0);
    this.torso.add(this.rightArm);

    // Head.
    const head = new THREE.Group();
    head.position.y = 0.83;
    this.torso.add(head);

    const skull = new THREE.Mesh(
      new THREE.SphereGeometry(0.16, 16, 12),
      skinMat
    );
    if (isAlien) skull.scale.set(0.82, 1.25, 0.95);
    head.add(skull);

    // Face details.
    const brow = new THREE.Mesh(
      new THREE.BoxGeometry(0.24, 0.03, 0.06),
      skinMat
    );
    brow.position.set(0, 0.04, 0.14);
    head.add(brow);

    const nose = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.08, 0.08),
      skinMat
    );
    nose.position.set(0, -0.015, 0.16);
    head.add(nose);

    const mouth = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.018, 0.03),
      createMatteMaterial({ color: 0x2a1e1a, roughness: 0.9 })
    );
    mouth.position.set(0, -0.07, 0.145);
    head.add(mouth);

    const eyeGeo = new THREE.SphereGeometry(0.019, 8, 6);
    if (isAlien && options.kind === 'alien-tall') {
      // 4 eyes for tall aliens.
      for (const side of [-1, 1] as const) {
        const top = new THREE.Mesh(eyeGeo, eyeMat);
        top.position.set(side * 0.055, 0.015, 0.14);
        head.add(top);
        const bot = new THREE.Mesh(eyeGeo, eyeMat);
        bot.position.set(side * 0.085, -0.08, 0.13);
        head.add(bot);
      }
    } else {
      const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
      eyeL.position.set(-0.055, 0.015, 0.145);
      head.add(eyeL);
      const eyeR = eyeL.clone();
      eyeR.position.x = 0.055;
      head.add(eyeR);
    }

    // Hair / hood / crest.
    if (isAlien) {
      const crest = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.22, 0.14),
        accentMat
      );
      crest.position.y = 0.14;
      head.add(crest);
    } else if (options.kind === 'human-engineer') {
      // Beanie/cap.
      const cap = new THREE.Mesh(
        new THREE.SphereGeometry(0.17, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2),
        accentMat
      );
      cap.scale.set(1.05, 0.65, 1.05);
      cap.position.y = 0.08;
      head.add(cap);
    } else {
      const hair = new THREE.Mesh(
        new THREE.SphereGeometry(0.17, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2),
        hairMat
      );
      hair.scale.set(1.02, 0.6, 1.04);
      hair.position.y = 0.08;
      head.add(hair);
    }

    // Backpack (30% of civilians).
    if (Math.random() < 0.4 && !isRobed) {
      const pack = new THREE.Mesh(
        new THREE.BoxGeometry(0.4, 0.5, 0.22),
        suitDarkMat
      );
      pack.position.set(0, 0.25, -0.2);
      this.torso.add(pack);

      const strap = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, 0.08, 0.06),
        accentMat
      );
      strap.position.set(0, 0.5, -0.08);
      this.torso.add(strap);
    }
  }

  private buildWanderArm(
    sleeveMat: THREE.Material,
    sleeveDarkMat: THREE.Material,
    skinMat: THREE.Material
  ): THREE.Group {
    const arm = new THREE.Group();

    const shoulder = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 10, 8),
      sleeveMat
    );
    arm.add(shoulder);

    const upper = new THREE.Mesh(
      new THREE.CylinderGeometry(0.075, 0.065, 0.4, 10),
      sleeveMat
    );
    upper.position.y = -0.25;
    arm.add(upper);

    const elbow = new THREE.Mesh(
      new THREE.SphereGeometry(0.07, 10, 8),
      sleeveDarkMat
    );
    elbow.position.y = -0.47;
    arm.add(elbow);

    const lower = new THREE.Mesh(
      new THREE.CylinderGeometry(0.065, 0.058, 0.35, 10),
      sleeveDarkMat
    );
    lower.position.y = -0.66;
    arm.add(lower);

    const hand = new THREE.Mesh(
      new THREE.SphereGeometry(0.065, 10, 8),
      skinMat
    );
    hand.position.y = -0.85;
    arm.add(hand);

    return arm;
  }

  update(dt: number): void {
    if (this.waypoints.length < 2) return;

    if (this.pauseTimer > 0) {
      this.pauseTimer -= dt;
      return;
    }

    const next = this.waypoints[(this.waypointIndex + 1) % this.waypoints.length]!;
    const pos = this.object3D.position;
    const dx = next.x - pos.x;
    const dz = next.z - pos.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.4) {
      this.waypointIndex = (this.waypointIndex + 1) % this.waypoints.length;
      this.pauseTimer = Math.random() * 1.6;
      return;
    }

    const step = Math.min(this.speed * dt, dist);
    pos.x += (dx / dist) * step;
    pos.z += (dz / dist) * step;

    const targetYaw = Math.atan2(dx, dz);
    const currentYaw = this.object3D.rotation.y;
    let delta = targetYaw - currentYaw;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    this.object3D.rotation.y += delta * Math.min(1, dt * 5);

    // Walk cycle.
    this.phase += dt * 5.5;
    const swing = Math.sin(this.phase) * 0.32;
    this.leftLeg.rotation.x = swing;
    this.rightLeg.rotation.x = -swing;
    this.leftArm.rotation.x = -swing * 0.6;
    this.rightArm.rotation.x = swing * 0.6;
    this.torso.position.y =
      (this.torso.userData['baseY'] ?? 1.05) +
      Math.abs(Math.sin(this.phase)) * 0.03;
  }
}
